import styled, { css } from 'styled-components';

import Icon from '@klaviyo-mkt/gatsby-components/atoms/icon';
import { Anchor } from '@klaviyo-mkt/gatsby-components/atoms/anchor';
import KlaviyoModal from '@klaviyo-mkt/gatsby-components/organisms/modal';
import { StyledDivider } from '@klaviyo-mkt/gatsby-components/utils/two-column-l30-r70/styles';

import { TableOfContents } from '../../../components/table-of-contents';
import TopicsMenuWithData from '../../../components/topics-menu';

const accordionStyles = css`
    details > summary {
      cursor: pointer;
      list-style-type: none;
    }

  .accordion {
    border-bottom: 1px solid ${({ theme }) => theme.colors.sandShade};

    .accordion__item {
      margin: 0;

      &[open] {
      .accordion__item-title::after {
        transform: rotate(180deg);
      }
    }
    }

    .accordion__item-title {
      margin-bottom: 0;
      padding: 1.6rem 5.6rem 1.6rem 0;
      position: relative;

      &::after {
        background-image: url("data:image/svg+xml,%3Csvg width='20' height='12' viewBox='0 0 20 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.842773 1L9.99974 10.157L19.1567 1' stroke='%23232426' stroke-width='1.5'/%3E%3C/svg%3E");
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        content: '';
        display: block;
        height: 2.4rem;
        position: absolute;
        right: 1.6rem;
        top: 1.8rem;
        transition-property: transform;
        transition-timing-function: ease-out;
        transition-duration: 350ms;
        width: 2.4rem;
      }
    }

    .accordion__item-content {
      overflow: hidden;
      padding: 0.8rem 1.6rem 1.6rem 0;
    }
  }
`;

const richTextStyles = css`
  *:not([class*="wistia"]) {
    margin-bottom: 1.6rem;
  }

  [class*="wistia"] * {
    margin-bottom: 0rem;
  }

  .wistia_responsive_padding {
    margin-bottom: 1.6rem;
  }

  a,
  p,
  ul {
    font-size: 1.6rem;
    line-height: 2.4rem;
  }

  a {
    color: ${({ theme }) => theme.colors.charcoal};

    &:hover {
      color: ${({ theme }) => theme.colors.charcoal};
    }
  }

  b,
  strong {
    font-weight: 500;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: 500;
    scroll-margin-top: 9.6rem;
  }

  h2 {
    font-size: 2.0rem;
    line-height: 2.4rem;
  }

  img {
    height: auto;
    margin: 4rem auto;
    max-width: 100%;

    &:hover {
      cursor: pointer;
    }
  }

  li {
    margin-top: 1.6rem;
  }

  table {
    margin-top: 1.6rem;

    td,
    th {
      padding: 0.8rem;
    }

    tbody,
    td,
    th {
      border: 1px solid ${({ theme }) => theme.colors.sandShade};
    }
  }

  h2:last-of-type ~ ul {
    /* to catch Additional resources */
    list-style-type: none;
    padding: 0;
  }

  .bs-callout {
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M11 6C11 6.55228 10.5523 7 10 7C9.44771 7 9 6.55228 9 6C9 5.44772 9.44771 5 10 5C10.5523 5 11 5.44772 11 6Z" fill="%231D1E20"/><path d="M8.5 8C7.94772 8 7.5 8.44771 7.5 9C7.5 9.55229 7.94772 10 8.5 10H9V13C8.44771 13 8 13.4477 8 14C8 14.5523 8.44772 15 9 15H11C11.5523 15 12 14.5523 12 14C12 13.4477 11.5523 13 11 13V8H8.5Z" fill="%231D1E20"/><path fill-rule="evenodd" clip-rule="evenodd" d="M0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10ZM10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2Z" fill="%231D1E20"/></svg>') no-repeat;
    background-position: top 10px left 16px;
    background-color: ${({ theme }) => theme.colors.lemon};
    border: 1px solid ${({ theme }) => theme.colors.charcoal};
    border-radius: 0.8rem;
    font-size: 1.6rem;
    line-height: 2.4rem;
    padding: 0.8rem 1.6rem;
    margin-top: 1.6rem;

    p {
      font-size: inherit;
      line-height: inherit;
      margin-left: 3.2rem;
    }
    
    & p:last-of-type {
      margin-bottom: 0;
    }

    ::before {
      display: block;
      margin-left: 3.2rem;
      font-weight: 500;
    }

    a {
      color: ${({ theme }) => theme.colors.charcoal};
      text-underline-position: under;
    }
  }

  .bs-callout-warning {
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="22" height="19" viewBox="0 0 22 19" fill="none"><path d="M12.0006 15C12.0006 15.5523 11.5529 16 11.0006 16C10.4484 16 10.0006 15.5523 10.0006 15C10.0006 14.4477 10.4484 14 11.0006 14C11.5529 14 12.0006 14.4477 12.0006 15Z" fill="%231D1E20"/><path d="M12.0006 12V6H10.0006V12H12.0006Z" fill="%231D1E20"/><path fill-rule="evenodd" clip-rule="evenodd" d="M9.25231 1.0878C10.0143 -0.283824 11.9869 -0.283821 12.7489 1.0878L21.0494 16.0287C21.79 17.3617 20.8261 19 19.3011 19H2.70014C1.17517 19 0.211238 17.3617 0.951828 16.0287L9.25231 1.0878ZM11.0006 2.05908L2.70014 17H19.3011L11.0006 2.05908Z" fill="%231D1E20"/></svg>') no-repeat;
    background-position: top 10px left 16px;
    background-color: var(--color-poppy-light);
  }

  .bs-callout-requirements {
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M15.7346 12L18.5679 7H4V17H18.5679L15.7346 12ZM22 19H2V5H22L18.0334 12L22 19Z" fill="%231D1E20"/></svg>') no-repeat;
    background-position: top 10px left 16px;
    background-color: var(--color-sand-shade);
  }

  pre {
    code {
      font-size: 1.2rem;
    }

    strong {
      font-weight: 600;
    }
  }

  ${accordionStyles}

  .visuallyHidden {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
`;

export const ArticlePage = styled.div`
  margin: 1.6rem 0 0 0;

  ${StyledDivider} {
    display: none;
  }

  ${({ theme }) => `
    ${theme.screenSize.s1024.mediaQuery} {
      margin: 5.6rem 0 0 0;

      ${StyledDivider} {
        display: block;
      }
    }
  `}
`;

export const ArticleHeader = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: 1 / -1;
  margin: 5rem 0;

  ${({ theme }) => `
    ${theme.screenSize.s1024.mediaQuery} {
      margin: 4rem 0;
    }
  `}
`;

export const LinkContainer = styled.span`
  display: inline-block;
`;

export const AnchorLink = styled(Icon)`
  display: inline-block;
  height: 1.6rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s linear .2s;
`;

export const Breadcrumbs = styled.div`
  display: flex;
  font-size: 1.4rem;
  gap: 0.8rem;
  line-height: 2.0rem;
  margin-bottom: 2.8rem;
`;

export const Slash = styled.div``;

export const Breadcrumb = styled(Anchor)``;

export const StyledTopicsMenu = styled(TopicsMenuWithData)`
  flex-shrink: 0;
`;

export const Content = styled.div``;

export const ArticleContent = styled.div`
  ${richTextStyles}

  ${LinkContainer} {
    margin: 0 0 0 0.8rem;
  }

  ${AnchorLink} {
    margin: 0;
  }

  h2:hover ${AnchorLink},
  h2:hover ${LinkContainer},
  h3:hover ${AnchorLink},
  h3:hover ${LinkContainer} {
    opacity: 1; 
    cursor: pointer;
  }
`;

export const StyledArticleTableOfContents = styled(TableOfContents)`
  display: none;
  top: 11.4rem;


  ${({ theme }) => `
    ${theme.screenSize.s1024.mediaQuery} {
      display: block;
    }
  `}
`;

export const StyledKlaviyoModal = styled(KlaviyoModal)`
  .modalContents,
  .mediaModalContents {
    max-height: none;
  }
`;

export const Image = styled.img`
  width: max-content;
`;

export const ImageContainer = styled.div`
  display: flex;
  justify-content: center
`;
